import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Icon, Loader, Popup } from "semantic-ui-react";

import { Dropdown, ReactTable } from "components";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOrderModal/components/ReplacementOrderTable/ReplacementOrderTable.scss";
import { REPLACEMENT_ORDER_STATUS } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOrderModal/hooks";
import { useTyreNet, useTyreTeam } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { getTyreSeasonIcon } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { DealerLocation } from "models";
import type { ReplacementOrder } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOrderModal/hooks";

type ReplacementOrderTableProps = {
  location: DealerLocation | undefined;
  loading: boolean;
  replacementOrders: ReplacementOrder[];
  handleQuantityUpdate: (order: ReplacementOrder, quantity: number) => void;
};

const columnHelper = createColumnHelper<ReplacementOrder>();

const renderStatus = (status?: REPLACEMENT_ORDER_STATUS) => {
  switch (status) {
    case REPLACEMENT_ORDER_STATUS.Pending:
      return <Loader active inline size="mini" />;
    case REPLACEMENT_ORDER_STATUS.Fulfilled:
      return <Icon className="check" color="green" />;
    case REPLACEMENT_ORDER_STATUS.Rejected:
      return <Icon className="close" color="red" />;
    default:
      return null;
  }
};

const getQuantityOptions = (stock: number) => Array.from({ length: stock + 1 }, (_, i) => ({ key: i, text: i, value: i }));

const isQuantityInvalid = (item: ReplacementOrder) => {
  if (item.quantity <= 0) return false;
  if (item.stock?.includes(">")) return false;
  return item.quantity > Number(item.stock ?? 0);
};

export const ReplacementOrderTable = ({ location, replacementOrders, loading, handleQuantityUpdate }: ReplacementOrderTableProps) => {
  const { t } = useTranslation();

  const tyreTeam = useTyreTeam(location);
  const tyreNet = useTyreNet();
  const { getDeliveryPopup, getDeliveryChannelName } = location?.is_tyre_team_enabled ? tyreTeam : tyreNet;

  const renderDeliveryPopup = (delivery?: string | null) => {
    const type = getDeliveryPopup(delivery);
    return type ? <Popup content={type.content} trigger={<span>{type.trigger}</span>} /> : null;
  };

  const columns = [
    columnHelper.accessor(row => row, {
      id: "season",
      header: "",
      size: 45,
      cell: ({ row: { original } }) => <Icon className={getTyreSeasonIcon(original.tyre?.season)} />
    }),
    columnHelper.accessor(row => row, {
      id: "description",
      size: 330,
      header: () => t("v8_description", "Description"),
      cell: ({ row: { original } }) => <Popup content={original.tyre?.description} trigger={<span>{original.tyre?.description}</span>} />
    }),
    columnHelper.accessor(row => row, {
      id: "brand",
      size: 120,
      header: () => t("v8_brand", "Brand"),
      cell: ({ row: { original } }) => original.tyre?.manufacturer
    }),
    columnHelper.accessor(row => row, {
      id: "artEan",
      size: 150,
      header: () => "Art / EAN #",
      cell: ({
        row: {
          original: { tyre_team_system_number, tyre }
        }
      }) => (
        <>
          {tyre_team_system_number}
          {tyre_team_system_number && tyre?.ean && " / "}
          {tyre?.ean && <Popup hoverable trigger={<span className="ean">{tyre.ean}</span>} content={tyre.ean} />}
        </>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "gross",
      size: 110,
      header: () => `${t("v8_gross", "Gross")} €`,
      cell: ({ row: { original } }) => (original.stock && original.gross_price ? formatPrice(original.gross_price) : "0.0")
    }),
    columnHelper.accessor(row => row, {
      id: "neto",
      size: 110,
      header: () => `${t("v8_neto", "Net")} €`,
      cell: ({ row: { original } }) => (original.stock && original.net_price ? formatPrice(original.net_price) : "0.0")
    }),
    columnHelper.accessor(row => row, {
      id: "load",
      size: 60,
      header: () => t("v8_load", "Load"),
      cell: ({ row: { original } }) => original.tyre?.carry_capacity
    }),
    columnHelper.accessor(row => row, {
      id: "speed",
      size: 60,
      header: () => <div className="th-speed">{t("v8_speed", "Speed")}</div>,
      cell: ({ row: { original } }) => original.tyre?.speed
    }),
    columnHelper.accessor(row => row, {
      id: "runflat",
      size: 100,
      header: () => <div className="th-runflat">{t("v8_runflat", "Runflat")}</div>,
      cell: ({ row: { original } }) => (original.tyre?.run_flat_tire ? <Icon name="check circle" color="green" /> : <Icon name="close" color="red" />)
    }),
    columnHelper.accessor(row => row, {
      id: "channel",
      size: 110,
      header: () => t("v8_channel", "Channel"),
      cell: ({ row: { original } }) => getDeliveryChannelName(original.tyre_team_channel)
    }),
    columnHelper.accessor(row => row, {
      id: "delivery",
      size: 160,
      header: () => t("v8_delivery", "Delivery"),
      cell: ({ row: { original } }) => renderDeliveryPopup(original.delivery_method)
    }),
    columnHelper.accessor(row => row, {
      id: "stock",
      size: 80,
      header: () => <div className="th-stock">{t("v8_stock", "Stock")}</div>,
      cell: ({ row: { original } }) => original.stock ?? 0
    }),
    columnHelper.accessor(row => row, {
      id: "quantity",
      size: 90,
      header: () => t("v8_quantity", "Quantity"),
      cell: ({ row: { original } }) => (
        <Dropdown
          disabled={loading}
          options={getQuantityOptions(original.not_ordered_yet_quantity)}
          value={original.quantity}
          onChange={data => handleQuantityUpdate(original, data ?? 0)}
          selection
          fluid
          className={isQuantityInvalid(original) ? "invalid-quantity" : ""}
        />
      )
    }),
    columnHelper.accessor(row => row.status, {
      id: "status",
      size: 75,
      header: () => t("v8_status", "Status"),
      cell: ({ getValue }) => renderStatus(getValue())
    })
  ];

  return (
    <div className="ReplacementOrderTable">
      <ReactTable isFetching={loading} data={replacementOrders} columns={columns} />
    </div>
  );
};
